import { Shield, Scale, AlertTriangle, FileText, CheckCircle, Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const DrugCharges = () => {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4 relative z-10 text-center">
          <Shield className="h-20 w-20 mx-auto mb-6 text-accent" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Drug Charge Defense</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            Aggressive Defense for Possession, Distribution, and Trafficking Charges
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <Link to="/appointment">Schedule Consultation</Link>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                Call: (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">Drug Crime Defense in Texas</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Texas drug laws are harsh. Even simple possession charges can result in jail time, heavy fines, and a permanent criminal record that affects employment, housing, and your future. Whether you're facing possession, distribution, manufacturing, or trafficking charges, you need experienced legal representation immediately.
            </p>

            <div className="bg-card p-8 rounded-lg shadow-lg mb-12">
              <h3 className="text-2xl font-bold mb-4">We Defend All Drug Charges:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Marijuana Possession",
                  "Cocaine Charges",
                  "Methamphetamine Offenses",
                  "Heroin/Opioid Crimes",
                  "Prescription Drug Fraud",
                  "Drug Trafficking",
                  "Drug Manufacturing",
                  "Possession with Intent to Distribute",
                  "Drug Paraphernalia",
                  "Federal Drug Charges"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Common Drug Charge Defenses</h3>
            <div className="space-y-6 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Scale className="h-6 w-6 text-accent" />
                  Illegal Search and Seizure
                </h4>
                <p className="text-muted-foreground">
                  The Fourth Amendment protects against unreasonable searches. If police violated your constitutional rights by searching without a valid warrant, probable cause, or consent, any evidence found can be suppressed. This often leads to case dismissal.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-6 w-6 text-accent" />
                  Lack of Possession or Knowledge
                </h4>
                <p className="text-muted-foreground">
                  Prosecution must prove you knowingly possessed the drugs. If drugs were found in a shared vehicle or residence, we argue you didn't know they were there or had no control over them.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6 text-accent" />
                  Chain of Custody Issues
                </h4>
                <p className="text-muted-foreground">
                  Evidence must be properly handled, documented, and stored. We investigate whether the prosecution can prove the drugs tested are actually the same drugs seized from you. Breaks in chain of custody can result in evidence being thrown out.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Shield className="h-6 w-6 text-accent" />
                  Lab Analysis Challenges
                </h4>
                <p className="text-muted-foreground">
                  Drug testing labs must follow strict protocols. We review lab procedures, qualifications of lab technicians, and accuracy of testing methods. Contamination, errors, or improper procedures can invalidate test results.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <FileText className="h-6 w-6 text-accent" />
                  Entrapment
                </h4>
                <p className="text-muted-foreground">
                  If law enforcement induced or pressured you into committing a drug offense you wouldn't have otherwise committed, you may have an entrapment defense.
                </p>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Texas Drug Penalty Groups</h3>
            <p className="text-muted-foreground mb-6">
              Texas classifies controlled substances into penalty groups, with Penalty Group 1 being the most serious:
            </p>
            <div className="space-y-4 mb-12">
              {[
                { group: "Penalty Group 1", drugs: "Cocaine, heroin, methamphetamine, GHB, ketamine, rohypnol" },
                { group: "Penalty Group 1-A", drugs: "LSD" },
                { group: "Penalty Group 2", drugs: "Ecstasy (MDMA), PCP, mescaline, psilocybin mushrooms" },
                { group: "Penalty Group 2-A", drugs: "Synthetic cannabinoids (K2, Spice)" },
                { group: "Penalty Group 3", drugs: "Valium, Xanax, Ritalin, anabolic steroids" },
                { group: "Penalty Group 4", drugs: "Compounds containing limited amounts of codeine or opium" },
                { group: "Marijuana", drugs: "Separate classification with its own penalties" }
              ].map((item, index) => (
                <div key={index} className="bg-card p-6 rounded-lg">
                  <h4 className="font-bold text-lg mb-2">{item.group}</h4>
                  <p className="text-muted-foreground">{item.drugs}</p>
                </div>
              ))}
            </div>

            <div className="bg-accent/10 border border-accent/20 p-6 rounded-lg mb-12">
              <h3 className="text-xl font-bold mb-3">Alternative Sentencing & Drug Courts</h3>
              <p className="text-muted-foreground mb-4">
                For first-time offenders or those with substance abuse issues, we explore alternatives to incarceration:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span><strong>Drug Court Programs</strong> - Treatment-based program with case dismissal upon completion</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span><strong>Deferred Adjudication</strong> - Complete probation and avoid conviction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span><strong>Pre-Trial Diversion</strong> - Complete program requirements for dismissal</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                  <span><strong>Probation</strong> - Community supervision instead of jail time</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <QuickContact 
        title="Facing Drug Charges? Get Help Now."
        description="Don't wait. The sooner we start your defense, the better your chances."
      />

      <CTASection 
        title="Protect Your Future"
        description="Drug convictions have lifelong consequences. Contact us for aggressive defense representation."
      />
      <Footer />
    </div>
  );
};

export default DrugCharges;
